import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useFormState } from 'react-use-form-state';
import cls from 'classnames';

import { SSOButtons } from 'src/explore/compositions/SSOButtons';
import { ErrorBox } from 'src/explore/elements/bs4/ErrorBox';
import { FormStateInput } from 'src/explore/elements/FormStateInput';
import { LoadingButton } from 'src/shoppe/elements/LoadingButton';

import * as authDataActionCreators from 'src/explore/data/auth/actions';

import { IShopConfig } from 'src/explore/types/store';
import { WarningLabel } from '../../elements/WarningLabel';

import styles from './index.module.sass';

type ILoginField = {
  email: string;
  password: string;
};

interface Props {
  account: boolean;
  config: IShopConfig;
  defaultEmail: string;
  loading: boolean;
  loginError?: string[];
  setAccount: ( account: boolean ) => void;
  setDefaultEmail: ( email: string ) => void;
  setLoading: ( loading: boolean ) => void;
  setLoginError: ( error: string[] | null ) => void;
  switchMode: ( mode: string ) => void;
  onForgotPasswordClicked: () => void;
  onSubmit: ( event: React.FormEvent, form: any ) => void;
}

export const Login: React.FC<Props> = ({
  account,
  config,
  defaultEmail,
  loading,
  loginError,
  setAccount,
  setDefaultEmail,
  setLoading,
  setLoginError,
  switchMode,
  onForgotPasswordClicked,
  onSubmit,
}) => {
  const emailRef = React.useRef<HTMLInputElement>( null );
  const passwordRef = React.useRef<HTMLInputElement>( null );

  const [ formState, { email, password }] = useFormState<ILoginField>({
    email: defaultEmail,
  });

  const dispatch = useDispatch();

  const handleSubmitEmail = () => {
    if ( !formState.values.email || formState.errors.email ) {
      return setLoginError( formState.errors.email ? [ formState.errors.email ] : null );
    }

    setLoginError( null );
    setLoading( true );

    dispatch<any>( authDataActionCreators.emailAuth( formState.values.email ))
      .then(() => {
        setAccount( true );
        setLoading( false );
        passwordRef.current?.focus();
      })
      .catch(() => {
        setLoginError([
          "Whoops, it looks like you don't have an account yet. You can create an account or try another email.",
        ]);
        setLoading( false );
      });
  };

  React.useEffect(() => {
    if ( !account ) {
      emailRef.current?.focus();
    }
  }, [ account ]);

  return (
    <>
      <div className="spec__login-form">
        {!!loginError && (
          <div className="row mb-4">
            <div className="col-12">
              <ErrorBox className="" errors={loginError} />
            </div>
          </div>
        )}

        {!account && (
          <form
            onSubmit={( event: React.FormEvent ) => {
              event.preventDefault();
              handleSubmitEmail();
            }}
          >
            {config && config.merchantAuthWarningLabel && (
              <WarningLabel label={config.merchantAuthWarningLabel} />
            )}

            <div className="row">
              <div className="col-12 form-group pb-0">
                <FormStateInput
                  {...email( 'email' )}
                  className={cls( loginError?.length && 'border-danger', 'fs-5' )}
                  id="email-address"
                  aria-label="Email Address"
                  placeholder="Email Address"
                  ref={emailRef}
                  required
                />
              </div>
            </div>
            <div className="row">
              <div className="col-6 form-group mt-2">
                <button
                  type="button"
                  className="btn btn-block py-3 font-weight-bold text-secondary"
                  onClick={() => switchMode( 'signup' )}
                >
                  Create Account
                </button>
              </div>
              <div className="col-6 form-group mt-2">
                <LoadingButton
                  className="btn btn-block py-3 font-weight-bold btn-secondary"
                  disabled={!formState.values.email}
                  loading={loading}
                  type="button"
                  onClick={handleSubmitEmail}
                >
                  Next
                </LoadingButton>
              </div>
            </div>
          </form>
        )}

        {account && (
          <>
            <form
              className={styles.password}
              onSubmit={( event: React.FormEvent ) => {
                onSubmit( event, formState );
              }}
            >
              <div className="row">
                <div className="col-12 form-group pb-0">
                  <FormStateInput
                    {...password( 'password' )}
                    className={cls( loginError?.length && 'border-danger', 'fs-5' )}
                    id="password"
                    aria-label="Password"
                    placeholder="Password"
                    ref={passwordRef}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6 form-group my-2 py-0">
                  <button
                    className="btn btn-block py-3 font-weight-bold text-secondary"
                    type="button"
                    onClick={() => {
                      setLoginError( null );
                      setAccount( false );
                    }}
                  >
                    Back
                  </button>
                </div>
                <div className="col-6 form-group my-2 py-0">
                  <LoadingButton
                    className="btn btn-block py-3 font-weight-bold btn-secondary"
                    disabled={!formState.values.password}
                    loading={loading}
                    type="submit"
                  >
                    Log In
                  </LoadingButton>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-inline font-weight-bold my-3 p-0 text-secondary"
                onClick={() => {
                  setDefaultEmail( formState.values.email );
                  onForgotPasswordClicked();
                }}
              >
                Forgot Password
              </button>
            </form>
          </>
        )}
      </div>
      <SSOButtons />
    </>
  );
};
